
import React, { useEffect, useRef } from 'react';
import Layout from '@/components/Layout/Layout';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PeriodCalculator from '@/components/SpecialCalculators/PregnancyCalculators/PeriodCalculator';

const PeriodCalculatorPage = () => {
  const mainContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to the top of the page when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="container mx-auto max-w-3xl px-4" ref={mainContentRef}>
        <div className="mb-6">
          <Link to="/special" className="flex items-center text-primary hover:underline mb-2">
            <ArrowLeft size={16} className="mr-1" />
            Back to Special Calculators
          </Link>
          <h1 className="text-3xl font-bold mb-2">Period Calculator</h1>
          <p className="text-gray-600 mb-6">
            Track and predict your menstrual cycle, fertility window, and ovulation dates.
          </p>
        </div>
        
        <div className="mb-8">
          <PeriodCalculator />
        </div>
        
        <div className="mt-10 prose max-w-none">
          <h2 className="text-xl font-semibold mb-4">Understanding Your Menstrual Cycle</h2>
          <p>
            The menstrual cycle is the regular natural change that occurs in the female reproductive 
            system, making pregnancy possible. The cycle is necessary for the production of eggs and 
            preparation of the uterus for pregnancy.
          </p>
          
          <h3 className="text-lg font-medium mt-6 mb-2">Phases of the Menstrual Cycle</h3>
          <p>
            A typical menstrual cycle has four phases:
          </p>
          <ol className="list-decimal ml-6">
            <li><strong>Menstruation:</strong> The shedding of the uterine lining (endometrium), resulting in your period. This typically lasts 3-7 days.</li>
            <li><strong>Follicular Phase:</strong> From the first day of your period until ovulation. During this time, follicles in your ovaries develop, and one becomes dominant.</li>
            <li><strong>Ovulation:</strong> The release of an egg from a dominant follicle. This typically occurs around day 14 in a 28-day cycle.</li>
            <li><strong>Luteal Phase:</strong> After ovulation until the start of your next period. If the egg isn't fertilized, hormone levels drop, triggering menstruation.</li>
          </ol>
          
          <h3 className="text-lg font-medium mt-6 mb-2">Benefits of Tracking Your Cycle</h3>
          <ul className="list-disc ml-6">
            <li><strong>Family planning:</strong> Whether trying to conceive or avoid pregnancy</li>
            <li><strong>Health awareness:</strong> Identify irregular cycles that might indicate health issues</li>
            <li><strong>Symptom management:</strong> Prepare for and manage PMS symptoms</li>
            <li><strong>Life planning:</strong> Schedule important events around your period if desired</li>
          </ul>
          
          <div className="bg-purple-50 p-4 rounded-md mt-6">
            <h3 className="text-lg font-medium text-purple-800 mb-2">Cycle Variations</h3>
            <p className="text-purple-800">
              While this calculator uses your average cycle length to make predictions, it's normal for 
              your cycle length to vary slightly from month to month. Many factors can influence your cycle, 
              including stress, illness, significant weight changes, travel, and exercise.
            </p>
          </div>
          
          <div className="bg-red-50 p-4 rounded-md mt-6">
            <h3 className="text-lg font-medium text-red-800 mb-2">When to See a Doctor</h3>
            <p className="text-red-800">
              Consult a healthcare provider if you experience:
            </p>
            <ul className="list-disc ml-6 text-red-800">
              <li>Very irregular periods</li>
              <li>No periods for 3 months or more</li>
              <li>Periods that last more than 7 days</li>
              <li>Extremely heavy bleeding</li>
              <li>Severe pain during periods</li>
              <li>Bleeding between periods</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PeriodCalculatorPage;
